import { Component, Inject, OnInit } from "@angular/core";

import { FormsModule } from "@angular/forms";
import {
  MatBottomSheetRef,
  MAT_BOTTOM_SHEET_DATA,
} from "@angular/material/bottom-sheet";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatSnackBar } from "@angular/material/snack-bar";
import { LucideAngularModule, X, Save } from 'lucide-angular';

export interface SettingsData {
  whatsappNumber: string;
}

@Component({
  selector: "app-chatbot-settings-bottom-sheet",
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    LucideAngularModule
],
  template: `
    <div class="settings-bottom-sheet p-2">
      <div class="flex items-center justify-between mb-1">
        <h2>
          Configuración
        </h2>
        <button
          mat-icon-button
          (click)="close()"
          class="text-light-text-tertiary hover:text-light-text-secondary dark:text-dark-text-tertiary dark:hover:text-dark-text-secondary"
        >
          <lucide-angular [img]="XIcon" class="w-5 h-5"></lucide-angular>
        </button>
      </div>

      <div class="space-y-2">
        <!-- WhatsApp Number Section -->
        <div>
          <mat-form-field appearance="fill" class="w-full">
            <mat-label>Número de WhatsApp</mat-label>
            <input
              matInput
              type="tel"
              [(ngModel)]="whatsappNumber"
              placeholder="+56912345678"
              pattern="\\+[1-9]\\d{1,14}"
              required
            />
            <mat-hint
              >Ingresa tu número con código de país (ej: +56912345678)</mat-hint
            >
          </mat-form-field>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-3 pt-4">
          <button
            mat-flat-button
            color="primary"
            (click)="save()"
            [disabled]="!isFormValid()"
            class="flex-1 py-2"
            [class.opacity-60]="!isFormValid()"
            [class.cursor-not-allowed]="!isFormValid()"
          >
            <div class="flex items-center justify-center gap-2">
              <lucide-angular [img]="SaveIcon" class="w-5 h-5"></lucide-angular>
              <span>Guardar</span>
            </div>
          </button>
          <button mat-stroked-button (click)="close()" class="flex-1 py-2">
            <div class="flex items-center justify-center gap-2">
              <lucide-angular [img]="XIcon" class="w-5 h-5"></lucide-angular>
              <span>Cancelar</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .settings-bottom-sheet {
        min-height: 300px;
        max-height: 80vh;
        overflow-y: auto;
      }

      ::ng-deep .mat-mdc-form-field {
        width: 100%;
      }

      .space-y-6 > * + * {
        margin-top: 1.5rem;
      }

      .flex {
        display: flex;
      }

      .items-center {
        align-items: center;
      }

      .justify-between {
        justify-content: space-between;
      }

      .gap-3 {
        gap: 0.75rem;
      }

      .flex-1 {
        flex: 1;
      }

      .pt-4 {
        padding-top: 1rem;
      }

      .mb-6 {
        margin-bottom: 1.5rem;
      }

      .p-6 {
        padding: 1.5rem;
      }

      .text-xl {
        font-size: 1.25rem;
      }

      .font-semibold {
        font-weight: 600;
      }

      .text-light-text-tertiary {
        color: var(--light-text-tertiary, rgb(107 114 128));
      }

      .hover\\:text-light-text-secondary:hover {
        color: var(--light-text-secondary, rgb(55 65 81));
      }

      :host-context(.dark) {
        .dark\\:text-dark-text-tertiary {
          color: var(--dark-text-tertiary, rgb(156 163 175));
        }

        .dark\\:hover\\:text-dark-text-secondary:hover {
          color: var(--dark-text-secondary, rgb(229 231 235));
        }
      }
    `,
  ],
})
export class ChatbotSettingsBottomSheetComponent implements OnInit {
  readonly XIcon = X;
  readonly SaveIcon = Save;
  whatsappNumber: string = "";

  constructor(
    private bottomSheetRef: MatBottomSheetRef<ChatbotSettingsBottomSheetComponent>,
    private snackBar: MatSnackBar,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: SettingsData,
  ) {}

  ngOnInit(): void {
    if (this.data) {
      this.whatsappNumber = this.data.whatsappNumber || "";
    }
  }

  isFormValid(): boolean {
    const phoneRegex = /^\+[1-9]\d{1,14}$/;
    const trimmedNumber = this.whatsappNumber?.trim() || "";
    return phoneRegex.test(trimmedNumber);
  }

  save(): void {
    if (!this.isFormValid()) {
      this.snackBar.open(
        "Por favor, completa todos los campos correctamente.",
        "Cerrar",
        {
          duration: 4000,
          horizontalPosition: "center",
          verticalPosition: "top",
          panelClass: ["error-snackbar"],
        },
      );
      return;
    }

    const result: SettingsData = {
      whatsappNumber: this.whatsappNumber.trim(),
    };

    this.bottomSheetRef.dismiss(result);

    this.snackBar.open("✅ Configuración guardada correctamente", "", {
      duration: 3000,
      horizontalPosition: "center",
      verticalPosition: "top",
      panelClass: ["success-snackbar"],
    });
  }

  close(): void {
    this.bottomSheetRef.dismiss();
  }
}
